// components/subscription/RequestForm/index.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../../hooks/useAuth";
import { 
    requestSubscription,
    selectSubscriptionLoading,
    selectSubscriptionError 
} from "../../../store/slices/subscriptionSlice";
import { getPlanInterval } from "../../../config/subscriptionPlans";
import { formatCurrency } from "../../../utils/format";
import Select from "../../common/Select";
import { Button, Input } from "../../../styles/components";
import { toast } from "sonner";
import {
    FormWrapper,
    FormHeader,
    FormGroup,
    PlanSummary,
    ModalContainer,
    ButtonGroup,
    CreditInfoSection,
    InfoText,
    PriceBreakdown,
    PriceBreakdownItem,
    PeriodInfo,
    ErrorMessage
} from "./styles";

const RequestForm = ({ selectedPlan, onBack }) => {
    const { user } = useAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const loading = useSelector(selectSubscriptionLoading);
    const error = useSelector(selectSubscriptionError);

    const [formData, setFormData] = useState({
        childCount: "1",
        preferredTime: "morning",
        preferredSessionDuration: selectedPlan.features.maxSessionDuration.toString(),
        referralCode: "",
        specialRequests: "",
    });

    const [calculatedPrice, setCalculatedPrice] = useState({
        basePrice: parseInt(selectedPlan.price.replace(/,/g, '')),
        discount: 0,
        finalPrice: parseInt(selectedPlan.price.replace(/,/g, ''))
    });

    useEffect(() => {
        calculatePrice(formData.childCount);
    }, [formData.childCount]);

    const calculatePrice = (childCount) => {
        const basePrice = parseInt(selectedPlan.price.replace(/,/g, ''));
        let discount = 0;

        if (childCount === "2") {
            discount = basePrice * 0.15; // 15% discount
        } else if (childCount === "3") {
            discount = basePrice * 0.25; // 25% discount
        }

        setCalculatedPrice({
            basePrice,
            discount,
            finalPrice: basePrice - discount
        });
    };

    const childCountOptions = [
        { value: "1", label: "1 Child" },
        { value: "2", label: "2 Children (15% discount)" },
        { value: "3", label: "3 or more Children (25% discount)" },
    ];

    const timeSlotOptions = [
        { value: "morning", label: "Morning (6 AM - 9 AM)" },
        { value: "afternoon", label: "Afternoon (11 AM - 2 PM)" },
        { value: "evening", label: "Evening (4 PM - 7 PM)" },
    ];

    const sessionDurationOptions = Array.from(
        { length: selectedPlan.features.maxSessionDuration },
        (_, i) => ({
            value: String(i + 1),
            label: `${i + 1} ${i + 1 === 1 ? "hour" : "hours"}`,
        })
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            const result = await dispatch(requestSubscription({
                userId: user.uid,
                planData: {
                    planId: selectedPlan.id,
                    periodCredits: selectedPlan.periodCredits,
                    basePrice: calculatedPrice.basePrice,
                    discount: calculatedPrice.discount,
                    finalPrice: calculatedPrice.finalPrice,
                    plan: selectedPlan,
                    ...formData,
                }
            })).unwrap();

            toast.success("Subscription request submitted successfully!", {
                style: { padding: "1rem" },
            });
            navigate("/dashboard");
        } catch (error) {
            toast.error(error.message || "Failed to submit request", {
                style: { padding: "1rem" },
            });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <ModalContainer>
            <FormWrapper
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <FormHeader>
                    <h2>Complete Your Subscription Request</h2>
                    <p>Fill in the details below to submit your request</p>
                </FormHeader>

                <PlanSummary>
                    <h3>Selected Plan: {selectedPlan.name}</h3>
                    <PeriodInfo interval={selectedPlan.interval}>
                        {getPlanInterval(selectedPlan.id)}
                    </PeriodInfo>
                    
                    <CreditInfoSection>
                        <div className="credit-detail">
                            <span className="label">Credits:</span>
                            <span className="value">
                                {selectedPlan.periodCredits} hours per {selectedPlan.interval}
                            </span>
                        </div>
                        <div className="credit-detail">
                            <span className="label">Additional Credits:</span>
                            <span className="value">
                                {formatCurrency(selectedPlan.features.addOnCredits.price)}/hour
                            </span>
                        </div>
                        <div className="credit-detail">
                            <span className="label">Max Session Duration:</span>
                            <span className="value">
                                {selectedPlan.features.maxSessionDuration} hours
                            </span>
                        </div>
                    </CreditInfoSection>

                    <PriceBreakdown>
                        <PriceBreakdownItem>
                            <span>Base Price:</span>
                            <span>{formatCurrency(calculatedPrice.basePrice)}</span>
                        </PriceBreakdownItem>
                        {calculatedPrice.discount > 0 && (
                            <PriceBreakdownItem isDiscount>
                                <span>Multi-child Discount:</span>
                                <span>-{formatCurrency(calculatedPrice.discount)}</span>
                            </PriceBreakdownItem>
                        )}
                        <PriceBreakdownItem isTotal>
                            <span>Total {selectedPlan.interval} payment:</span>
                            <span>{formatCurrency(calculatedPrice.finalPrice)}</span>
                        </PriceBreakdownItem>
                    </PriceBreakdown>
                </PlanSummary>

                <form onSubmit={handleSubmit}>
                    <FormGroup>
                        <label htmlFor="childCount">Number of Children</label>
                        <Select
                            id="childCount"
                            name="childCount"
                            value={formData.childCount}
                            onChange={handleChange}
                            options={childCountOptions}
                        />
                        <InfoText>
                            Multi-child discounts apply to subscription price
                        </InfoText>
                    </FormGroup>

                    <FormGroup>
                        <label htmlFor="preferredSessionDuration">
                            Preferred Session Duration
                        </label>
                        <Select
                            id="preferredSessionDuration"
                            name="preferredSessionDuration"
                            value={formData.preferredSessionDuration}
                            onChange={handleChange}
                            options={sessionDurationOptions}
                        />
                        <InfoText>
                            You can adjust this for each session when booking
                        </InfoText>
                    </FormGroup>

                    <FormGroup>
                        <label htmlFor="preferredTime">Preferred Session Time</label>
                        <Select
                            id="preferredTime"
                            name="preferredTime"
                            value={formData.preferredTime}
                            onChange={handleChange}
                            options={timeSlotOptions}
                        />
                    </FormGroup>

                    <FormGroup>
                        <label htmlFor="referralCode">Referral Code (Optional)</label>
                        <Input
                            id="referralCode"
                            name="referralCode"
                            value={formData.referralCode}
                            onChange={handleChange}
                            placeholder="Enter referral code"
                        />
                    </FormGroup>

                    <FormGroup>
                        <label htmlFor="specialRequests">
                            Special Requests (Optional)
                        </label>
                        <Input
                            as="textarea"
                            id="specialRequests"
                            name="specialRequests"
                            value={formData.specialRequests}
                            onChange={handleChange}
                            placeholder="Any special requirements or requests"
                            rows={3}
                        />
                    </FormGroup>

                    {error && <ErrorMessage>{error}</ErrorMessage>}

                    <ButtonGroup>
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={onBack}
                            disabled={loading}
                        >
                            Back
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading ? "Submitting..." : "Submit Request"}
                        </Button>
                    </ButtonGroup>
                </form>
            </FormWrapper>
        </ModalContainer>
    );
};

export default RequestForm;